import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-community/core'
import {
    AgGridColumnType,
    AgGridExportType,
    ExportedColDef,
    ExportedColGroupDef
} from '@/shared/models/ag-grid.model'
import { StatisticalAnalysisDatasource } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { Nullable } from '@/shared/types/app.types'
import { StatiscalAnalysisTypeCases } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { StatisticalAnalysisActivityStatisticsDossierBaseRequest, StatisticalAnalysisActivityStatisticsDossierDetailRequest, StatisticalAnalysisActivityStatisticsDossierDetailViewTable, StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase, StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum } from '@/modules/statistical-analysis-module/services/activity-dossier/activity-dossier.dto'
import { StatisticalAnalysisActivityDossierExternalInfo } from '@/modules/statistical-analysis-module/views/service-activity-dossier/service-activity-dossier.model'
import { activityStatisticsDossierService } from '@/modules/statistical-analysis-module/services/activity-dossier/activity-dossier.service'
import { ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { concat, forEach } from 'lodash'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'

export class StatisticalAnalysisHandlerDatasourceActivityDossier extends StatisticalAnalysisDatasource<StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
    }

    public getGridColumns(): ColDef[] | ColGroupDef[] | ExportedColDef [] | ExportedColGroupDef [] {
        return concat([], [{ type: AgGridColumnType.CUSTOM_CHECKBOX } as ColDef], this._filterGroupedGridColumns(), this._getInfoColumns())
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisActivityDossierExternalInfo
        const baseRequest: StatisticalAnalysisActivityStatisticsDossierBaseRequest = activityStatisticsDossierService.getBaseRequest(sidebarFilter)
        const detailRequest: StatisticalAnalysisActivityStatisticsDossierDetailRequest = {
            groupByFields: (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        }
        const entityTypes = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases

        const response: StatisticalAnalysisActivityStatisticsDossierDetailViewTable = await activityStatisticsDossierService.getDetailViewTable(filterModel, params, entityTypes, baseRequest, detailRequest, scrollId)
        return {
            content: response?.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response?.scrollPage?.size,
                totalElements: null
            }
        }
    }

    private _getInfoColumns(): ExportedColGroupDef [] {
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.statistics'),
            children: [
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.caseNumbers'),
                    field: 'totalCases',
                    exportType: AgGridExportType.NUMBER,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.valueUnitTotal'),
                    field: 'totalURV',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.workUnitTotal'),
                    field: 'totalUCL',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.macroscopyWorkUnitTotal'),
                    field: 'macroUCL',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.reportWorkUnitTotal'),
                    field: 'reportUCL',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                }]
        }]
    }

    private _getGroupingColumns(): ExportedColDef [] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.caseType'),
                field: 'caseType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.priority'),
                field: 'priority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingDepartment'),
                field: 'physicianDepartment.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingCenter'),
                field: 'physicianCenter.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingHealthManagementArea'),
                field: 'physicianHealthManagementArea.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.regime'),
                field: 'regime.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.resident'),
                field: 'resident.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.laboratory'),
                field: 'laboratory.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.pathologist'),
                field: 'pathologist.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingProfessional'),
                field: 'physicianPetitioner.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('statisticalAnalysis.grids.consultationCase'),
                field: 'caseOfReference.literal',
                exportType: AgGridExportType.BOOLEAN,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }
        ]
    }

    private _filterGroupedGridColumns(): ColGroupDef[] {
        const colDefs: ColDef[] = []
        const groupedColumns = this._getGroupingColumns()
        forEach(this.statisticalAnalysisFilters?.grouping, (item: StatisticalAnalysisGroupingResponseField) => {
            switch (item.fieldName as StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum) {
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.CASE_TYPE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'caseType.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.LABORATORY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'laboratory.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PATHOLOGIST:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'pathologist.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PHYSICIAN_DEPARTMENT:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianDepartment.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PHYSICIAN_CENTER:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianCenter.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PHYSICIAN_HEALTH_MANAGEMENT_AREA:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianHealthManagementArea.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PRIORITY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'priority.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.REGIME:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'regime.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.RESIDENT:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'resident.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.PHYSICIAN_PETITIONER:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianPetitioner.literal') as ColDef)
                break
            case StatisticalAnalysisActivityStatisticsDossierGroupColumnsEnum.CASE_OF_REFERENCE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'caseOfReference.literal') as ColDef)
                break
            default: break
            }
        })
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.groupings'),
            children: colDefs
        }]
    }
}
